import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { EmptyState, ErrorState, LoadingState, Notice, PageHeader, Spinner, StatusBadge } from "@/components/common/States";
import { api, errorMessage, fetcher } from "@/lib/api";
import { RESELLER_STATUS, rupiah, timeAgo } from "@/lib/labels";

const PG_STATUS = {
  baru: { label: "Baru", tone: "warn" },
  diterima: { label: "Diterima", tone: "ok" },
  ditolak: { label: "Ditolak", tone: "danger" },
};

const TABS = [
  { key: "semua", label: "Semua" },
  { key: "baru", label: "Baru" },
  { key: "diterima", label: "Diterima" },
  { key: "ditolak", label: "Ditolak" },
];

const SumberBadge = ({ pg }) => (
  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${
    pg.source === "reseller" ? "bg-blue-50 text-[#0B3D91] dark:bg-blue-950 dark:text-blue-200" : "bg-muted text-muted-foreground"}`}
    data-testid={`pengajuan-source-${pg.id}`}>
    {pg.source === "reseller" ? `Via reseller ${pg.reseller_name}` : "Landing page"}
  </span>
);

const Stat = ({ label, value, tone = "text-foreground", testId }) => (
  <div className="rounded-2xl border bg-card p-5" data-testid={testId}>
    <p className={`font-brand text-2xl md:text-3xl font-extrabold ${tone}`}>{value}</p>
    <p className="text-sm font-medium mt-0.5">{label}</p>
  </div>
);

function TerimaDialog({ pg, open, onOpenChange }) {
  const qc = useQueryClient();
  const resellers = useQuery({ queryKey: ["billing", "resellers"], queryFn: () => fetcher("/platform/billing/resellers"), enabled: open });
  const settings = useQuery({ queryKey: ["billing", "settings"], queryFn: () => fetcher("/platform/billing/settings"), enabled: open });
  const daftar = (resellers.data ?? []).filter((r) => r.status === "Aktif");
  const [resellerId, setResellerId] = useState(pg.reseller_id ?? "");
  const [harga, setHarga] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dups, setDups] = useState(null);
  const cap = settings.data?.retail_price_cap_idr;
  const dipilih = daftar.find((r) => r.id === resellerId);

  const pilihReseller = (id) => {
    setResellerId(id);
    setHarga("");
    setDups(null);
    setErr("");
  };

  const submit = async (confirm) => {
    if (!resellerId) return setErr("Pilih reseller penguasa sekolah ini terlebih dahulu.");
    const hargaNum = harga.trim() === "" ? null : Number(harga);
    if (hargaNum !== null && (!Number.isFinite(hargaNum) || hargaNum < 0)) return setErr("Harga jual harus angka yang sah.");
    if (hargaNum !== null && cap && hargaNum > cap) {
      return setErr(`Harga jual Rp${hargaNum.toLocaleString("id-ID")} melebihi plafon Rp${cap.toLocaleString("id-ID")} per akun per bulan.`);
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/platform/pengajuan/${pg.id}/terima`, {
        reseller_id: resellerId, retail_price_idr: hargaNum, confirm_duplicate: confirm, note: note.trim() || null,
      });
      toast.success(`Sekolah ${data.school.name} dibuat (ID ${data.school.id}) di binaan ${data.reseller_name}.`);
      await qc.invalidateQueries({ queryKey: ["pengajuan"] });
      await qc.invalidateQueries({ queryKey: ["billing"] });
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.candidates) setDups({ ...d });
      else setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setDups(null); setErr(""); } onOpenChange(o); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="terima-dialog">
        <DialogHeader>
          <DialogTitle>Terima pengajuan — {pg.school_name}</DialogTitle>
          <DialogDescription>
            Sekolah baru dibuat dengan status Menunggu Verifikasi, lalu di-asuhkan ke reseller pilihan Anda.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <p className="font-semibold">{pg.school_name}</p>
            <p className="text-muted-foreground">{pg.city} · {pg.jenjang}{pg.student_count ? ` · ±${pg.student_count} siswa` : ""}</p>
            <p className="mt-1">{pg.pic_name}{pg.pic_role ? ` — ${pg.pic_role}` : ""} · {pg.phone}</p>
          </div>
          <div className="space-y-2">
            <Label>Reseller penguasa *</Label>
            <Select value={resellerId || undefined} onValueChange={pilihReseller}>
              <SelectTrigger aria-label="Reseller penguasa" data-testid="terima-reseller-select"><SelectValue placeholder="Pilih reseller" /></SelectTrigger>
              <SelectContent>
                {daftar.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}{r.is_platform ? " (milik founder)" : ""} — {rupiah(r.retail_price_idr)}/akun</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!resellers.isLoading && !daftar.length && (
              <p className="text-xs text-[#E11D2E]">Belum ada reseller aktif. Aktifkan reseller terlebih dahulu di menu Langganan.</p>
            )}
          </div>
          {dipilih && (
            <div className="space-y-2">
              <Label htmlFor="terima-harga">Harga jual per akun per bulan</Label>
              <Input id="terima-harga" inputMode="numeric" value={harga} placeholder={String(dipilih.retail_price_idr)}
                onChange={(e) => setHarga(e.target.value)} data-testid="terima-price-input" />
              <p className="text-xs text-muted-foreground">
                Harga jual {dipilih.name} saat ini {rupiah(dipilih.retail_price_idr)}.{cap ? ` Plafon ${rupiah(cap)}.` : ""}{" "}
                Kosongkan bila tidak berubah. Jika diubah, berlaku untuk seluruh sekolah binaan reseller ini.
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="terima-note">Catatan keputusan</Label>
            <Textarea id="terima-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Opsional — tersimpan pada pengajuan dan log audit." data-testid="terima-note-input" />
          </div>
          {err && <Notice tone="danger" testId="terima-error">{err}</Notice>}
          {dups && (
            <Notice tone="warn" testId="terima-duplicate-warning">
              <p className="font-semibold">{dups.message}</p>
              <ul className="mt-1 list-disc pl-5">
                {dups.candidates.map((c) => (
                  <li key={c.id}>{c.name} · {c.city} · cocok pada {c.match === "npsn" ? "NPSN" : "nama dan kota"}</li>
                ))}
              </ul>
            </Notice>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="terima-cancel">Batal</Button>
          {dups?.code === "possible_duplicate" ? (
            <Button onClick={() => submit(true)} disabled={busy} data-testid="terima-confirm-duplicate">
              {busy && <Spinner />} Tetap buat sekolah ini
            </Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={busy || !daftar.length} data-testid="terima-submit">
              {busy && <Spinner />} Buat sekolah & terima
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const KartuPengajuan = ({ pg, onTerima, onTolak }) => (
  <article className="rounded-xl border bg-card p-4 flex flex-col gap-2.5" data-testid={`pengajuan-item-${pg.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-bold leading-snug">{pg.school_name}</p>
        <p className="text-xs text-muted-foreground">
          {pg.city} · {pg.jenjang}{pg.student_count ? ` · ±${pg.student_count} siswa` : ""}
        </p>
      </div>
      <StatusBadge map={PG_STATUS} value={pg.status} testId={`pengajuan-status-${pg.id}`} />
    </div>
    <div className="text-sm">
      <p className="font-medium">{pg.pic_name}{pg.pic_role ? ` — ${pg.pic_role}` : ""}</p>
      <p className="text-xs text-muted-foreground break-all">{pg.phone} · {pg.email}</p>
    </div>
    {pg.message && <p className="text-sm text-muted-foreground border-l-2 border-border pl-3 italic">“{pg.message}”</p>}
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
      <SumberBadge pg={pg} />
      <span>{timeAgo(pg.created_at)}</span>
    </div>
    {pg.status === "baru" ? (
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" onClick={() => onTerima(pg)} data-testid={`pengajuan-terima-${pg.id}`}>Terima</Button>
        <Button size="sm" variant="outline" onClick={() => onTolak(pg)} data-testid={`pengajuan-tolak-${pg.id}`}>Tolak</Button>
      </div>
    ) : (
      <div className="rounded-lg bg-muted/50 p-2.5 text-xs text-muted-foreground" data-testid={`pengajuan-decision-${pg.id}`}>
        {pg.status === "diterima"
          ? <>Diterima{pg.decided_by ? ` oleh ${pg.decided_by}` : ""} → sekolah <span className="font-mono">{pg.school_id}</span> di binaan {pg.reseller_name}.</>
          : <>Ditolak{pg.decided_by ? ` oleh ${pg.decided_by}` : ""}: {pg.decision_reason}</>}
      </div>
    )}
  </article>
);

export default function PlatformPengajuan() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["pengajuan", "semua"], queryFn: () => fetcher("/platform/pengajuan?limit=200") });
  const [tab, setTab] = useState("semua");
  const [terimaFor, setTerimaFor] = useState(null);
  const [tolakFor, setTolakFor] = useState(null);

  const items = q.data?.items ?? [];
  const counts = q.data?.counts ?? {};
  const tampil = tab === "semua" ? items : items.filter((p) => p.status === tab);

  const tolak = async (reason) => {
    await api.post(`/platform/pengajuan/${tolakFor.id}/tolak`, { reason });
    toast.success(`Pengajuan ${tolakFor.school_name} ditolak. Reseller pemberi pengajuan diberi tahu.`);
    await qc.invalidateQueries({ queryKey: ["pengajuan"] });
  };

  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Pusat Kendali SIST" title="Pengajuan"
        description="Permintaan uji coba dari landing page dan pengajuan sekolah dari reseller. Terima untuk menjadikan sekolah binaan, atau tolak dengan alasan." />

      <div className="grid gap-3 sm:grid-cols-3 mb-5">
        <Stat label="Menunggu keputusan" value={counts.baru ?? 0}
          tone={(counts.baru ?? 0) > 0 ? "text-[#E11D2E]" : "text-foreground"} testId="pengajuan-stat-baru" />
        <Stat label="Diterima" value={counts.diterima ?? 0} testId="pengajuan-stat-diterima" />
        <Stat label="Ditolak" value={counts.ditolak ?? 0} testId="pengajuan-stat-ditolak" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="pengajuan-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`pengajuan-tab-${t.key}`}>
              {t.label}
              {t.key !== "semua" && counts[t.key] ? <span className="ml-1.5 text-xs text-muted-foreground">{counts[t.key]}</span> : null}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {q.isLoading ? (
        <LoadingState />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => qc.invalidateQueries({ queryKey: ["pengajuan"] })} />
      ) : !tampil.length ? (
        <EmptyState
          title={tab === "baru" ? "Tidak ada pengajuan yang menunggu" : "Belum ada data pada filter ini"}
          description="Pengajuan dari landing page dan dari reseller akan muncul di sini beserta notifikasi lonceng." />
      ) : (
        <div className="grid gap-3">
          {tampil.map((pg) => (
            <KartuPengajuan key={pg.id} pg={pg} onTerima={setTerimaFor} onTolak={setTolakFor} />
          ))}
        </div>
      )}

      {terimaFor && <TerimaDialog key={terimaFor.id} pg={terimaFor} open={!!terimaFor} onOpenChange={(o) => !o && setTerimaFor(null)} />}
      <ConfirmReasonDialog open={!!tolakFor} onOpenChange={(o) => !o && setTolakFor(null)}
        title={`Tolak pengajuan ${tolakFor?.school_name ?? ""}`}
        description="Alasan ini dilihat reseller pemberi pengajuan (bila ada) lewat notifikasi Ruang Mitra."
        confirmLabel="Tolak pengajuan" danger testId="tolak-dialog" onConfirm={tolak} />
    </div>
  );
}
